/** WS PING interval for link quality (seconds). */
export const FPS_PING_INTERVAL_SEC = 3;

/** How often demos refresh displayed link quality (milliseconds). */
export const FPS_LINK_REFRESH_MS = 3000;

/**
 * Enables WS application PING on a WebSocket builder (SDK 0.4+).
 *
 * @param {import("@fastpubsub-sdk/client").FastPubSubBuilder} builder
 * @returns {import("@fastpubsub-sdk/client").FastPubSubBuilder}
 */
export function withWsLinkQuality(builder) {
  return builder.pingIntervalSec(FPS_PING_INTERVAL_SEC);
}

/**
 * Formats `linkQuality()` for status lines.
 *
 * @param {{ lastRttMs?: number | undefined, medianRttMs?: number | undefined }} snapshot
 * @returns {string}
 */
export function formatLinkQuality(snapshot) {
  const last = snapshot?.lastRttMs;
  const median = snapshot?.medianRttMs;
  if (last == null && median == null) {
    return "ws rtt …";
  }
  const parts = [];
  if (last != null) {
    parts.push(`last ${last} ms`);
  }
  if (median != null) {
    parts.push(`median ${median} ms`);
  }
  return `ws ${parts.join(", ")}`;
}

/**
 * Reads link quality from a connected FastPubSub client.
 *
 * @param {import("@fastpubsub-sdk/client").FastPubSubClient | null | undefined} client
 * @returns {{ lastRttMs: number | undefined, medianRttMs: number | undefined }}
 */
export function readLinkQuality(client) {
  if (!client || typeof client.linkQuality !== "function") {
    return { lastRttMs: undefined, medianRttMs: undefined };
  }
  return client.linkQuality(30);
}

/**
 * Polls `client.linkQuality()` on a fixed interval.
 *
 * @param {import("@fastpubsub-sdk/client").FastPubSubClient | null | undefined} client
 * @param {(snapshot: { lastRttMs: number | undefined, medianRttMs: number | undefined }) => void} onSnapshot
 * @param {number} [intervalMs]
 * @returns {number}
 */
export function startLinkQualityRefresh(client, onSnapshot, intervalMs = FPS_LINK_REFRESH_MS) {
  const tick = () => onSnapshot(readLinkQuality(client));
  tick();
  return window.setInterval(tick, intervalMs);
}
